import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ban, Copy, KeyRound, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ativo: { label: "Ativo", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  expirado: { label: "Expirado", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
  consumido: { label: "Consumido", cls: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30" },
  revogado: { label: "Revogado", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
};

const fmtD = (iso) => {
  if (!iso) return "—";
  const [a, m, d] = String(iso).split("-");
  return `${d}/${m}/${String(a).slice(2)}`;
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function ConvitesAcessoPage() {
  const [dados, setDados] = useState(null);
  const [escopo, setEscopo] = useState("ativos");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [erro, setErro] = useState("");
  const [revogando, setRevogando] = useState(null);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const params = { escopo };
      if (escopo === "todos") {
        if (de) params.de = de;
        if (ate) params.ate = ate;
      }
      const { data } = await api.get("/tenant/chave-virtual/convites", { params });
      setDados(data);
    } catch (e) {
      setErro(errMsg(e));
      setDados({ convites: [], total: 0 });
    }
  }, [escopo, de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const copiar = async (c) => {
    try {
      await navigator.clipboard.writeText(c.link_pwa);
      toast.success("Link do convite copiado.");
    } catch { toast.error("Não foi possível copiar."); }
  };

  const confirmarRevogar = async () => {
    if (!revogando || processando) return;
    setProcessando(true);
    try {
      await api.post(`/tenant/chave-virtual/convites/${revogando.id}/revogar`);
      toast.success(`Convite de ${revogando.visitante_nome} revogado.`);
      setRevogando(null);
      carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setProcessando(false); }
  };

  const convites = dados?.convites || [];

  return (
    <div data-testid="convites-acesso-page">
      <PageHeader title="Convites de Acesso"
        subtitle="Chaves virtuais de visitantes emitidas pelo appmobile (Chave Virtual)">
        <Button variant="outline" size="sm" onClick={carregar} data-testid="convites-atualizar"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 mb-4 flex flex-wrap items-end gap-4"
        data-testid="convites-filtros">
        <div className="flex gap-2">
          {[["ativos", "Apenas ativos"], ["todos", "Todos"]].map(([v, rot]) => (
            <button key={v} type="button" data-testid={`convites-escopo-${v}`}
              onClick={() => setEscopo(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                escopo === v
                  ? "bg-indigo-600/20 border-indigo-500 text-indigo-300"
                  : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
              {rot}
            </button>
          ))}
        </div>
        {escopo === "todos" && (
          <>
            <div>
              <Label className="text-xs text-zinc-400">Válidos a partir de</Label>
              <Input type="date" data-testid="convites-filtro-de" value={de}
                onChange={(e) => setDe(e.target.value)}
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 w-40" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">até</Label>
              <Input type="date" data-testid="convites-filtro-ate" value={ate}
                onChange={(e) => setAte(e.target.value)}
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 w-40" />
            </div>
          </>
        )}
      </div>

      {!dados && <Spinner />}
      {dados && erro && (
        <EmptyState icon={KeyRound} title="Não foi possível carregar os convites" subtitle={erro} />
      )}
      {dados && !erro && convites.length === 0 && (
        <EmptyState icon={KeyRound}
          title={escopo === "ativos" ? "Nenhum convite ativo" : "Nenhum convite no período"}
          subtitle="Convites criados pelos moradores no appmobile aparecem aqui." />
      )}
      {dados && !erro && convites.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="convites-tabela">
            <thead>
              <tr className="text-left text-xs text-zinc-400 border-b border-zinc-800">
                <th className="px-4 py-3 font-medium">Visitante</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium">Janela de validade</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Usos</th>
                <th className="px-4 py-3 font-medium">Último uso</th>
                <th className="px-4 py-3 font-medium">Criado por (auditoria)</th>
                <th className="px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {convites.map((c) => {
                const st = STATUS[c.status] || { label: c.status, cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" };
                return (
                  <tr key={c.id} className="border-b border-zinc-800/60 last:border-0"
                    data-testid={`convites-row-${c.id}`}>
                    <td className="px-4 py-3 text-zinc-100 font-medium">{c.visitante_nome || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">{c.visitavip === 1 ? "Hóspede" : "Prestador"}</td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">
                      {fmtD(c.data_inicial)}{c.hora_inicial ? ` ${c.hora_inicial}` : ""}
                      {" → "}{fmtD(c.data_final)}{c.hora_final ? ` ${c.hora_final}` : ""}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${st.cls}`}
                        data-testid={`convites-status-${c.id}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{c.usos_realizados ?? 0}{c.max_usos ? `/${c.max_usos}` : ""}</td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{fmtDT(c.ultimo_uso_em)}</td>
                    <td className="px-4 py-3 text-zinc-300">
                      {c.criado_por_nome ? (
                        <>
                          <span className="block">{c.criado_por_nome}</span>
                          <span className="block text-[11px] text-zinc-500">
                            {c.eql ? `EQL ${c.eql} · ` : ""}{fmtDT(c.criado_em)}
                          </span>
                        </>
                      ) : <span className="text-zinc-500">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {c.link_pwa && (
                          <Button variant="outline" size="sm" data-testid={`convites-copiar-${c.id}`}
                            onClick={() => copiar(c)}
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8 px-2">
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {c.status === "ativo" && (
                          <Button variant="outline" size="sm" data-testid={`convites-revogar-${c.id}`}
                            onClick={() => setRevogando(c)}
                            className="border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 px-2">
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!revogando} onOpenChange={(o) => !o && !processando && setRevogando(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="convites-revogar-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Revogar convite</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Revogar o convite de <b className="text-zinc-200">{revogando?.visitante_nome}</b>?
              O link para de funcionar imediatamente. A ação fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" data-testid="convites-revogar-cancelar"
              onClick={() => setRevogando(null)} disabled={processando}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button data-testid="convites-revogar-confirmar" onClick={confirmarRevogar}
              disabled={processando} className="bg-red-600 hover:bg-red-500 text-white">
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sim, revogar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
